import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search, Save, AlertCircle, Trophy, Users, X, QrCode } from 'lucide-react';
import { QrScanner } from '@yudiel/react-qr-scanner';
import { supabase } from '../../lib/supabase';
import { Competition, Result, TeamResult } from '../../types/models';
import { getActiveSportsYear } from '../../services/sportsYearService';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const MobileResults: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [referenceNumber, setReferenceNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [participants, setParticipants] = useState<any[]>([]);
  const [positions, setPositions] = useState<Record<string, number | null>>({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [canManageResults, setCanManageResults] = useState(false);
  const [existingResults, setExistingResults] = useState<Result[]>([]);
  const [showScanner, setShowScanner] = useState(false);
  
  const [houses, setHouses] = useState<any[]>([]);
  const [teamPositions, setTeamPositions] = useState<Record<string, number | null>>({});

  useEffect(() => {
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { data: userRoles } = await supabase
        .from('user_roles')
        .select(`
          roles!inner(
            role_name
          )
        `)
        .eq('user_id', user?.id);

      const hasAccess = userRoles?.some(ur => 
        ur.roles.role_name === 'admin' || 
        ur.roles.role_name === 'manager' ||
        ur.roles.role_name === 'referee'
      ) ?? false;

      setCanManageResults(hasAccess);
      if (!hasAccess) {
        navigate('/');
      }
    } catch (error) {
      console.error('Error checking permissions:', error);
      setError('Failed to verify access permissions');
    }
  };

  const clearSearch = () => {
    setReferenceNumber('');
    setVerificationCode('');
    setIsVerified(false);
    setError(null);
    setCompetition(null);
    setParticipants([]);
    setPositions({});
    setTeamPositions({});
    setSuccess(false);
    setExistingResults([]);
    setShowScanner(false);
  };

  const handleDecode = (result: string) => {
    setReferenceNumber(result);
    setShowScanner(false);
  };

  const handleError = (error: Error) => {
    console.error('QR Scan Error:', error);
    setError(t('mobileResults.errors.scanError'));
  };

  const verifyCode = async () => {
    if (!referenceNumber.trim()) {
      setError(t('mobileResults.errors.enterReference'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data: competition, error: competitionError } = await supabase
        .from('events')
        .select('verification_code')
        .eq('reference_number', referenceNumber.trim())
        .single();

      if (competitionError) throw competitionError;
      if (!competition) throw new Error(t('mobileResults.errors.competitionNotFound'));

      if (!verificationCode.trim()) {
        setIsVerified(false);
        searchCompetition();
        return;
      }

      if (verificationCode.trim() !== competition.verification_code) {
        setError(t('mobileResults.errors.invalidCode'));
        return;
      }

      setIsVerified(true);
      searchCompetition();
    } catch (error: any) {
      console.error('Error verifying code:', error);
      setError(error.message || t('mobileResults.errors.verificationFailed'));
      setIsVerified(false);
    } finally {
      setLoading(false);
    }
  };

  const searchCompetition = async () => {
    if (!referenceNumber.trim()) {
      setError(t('mobileResults.errors.enterReference'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setCompetition(null);
      setParticipants([]);
      setPositions({});
      setTeamPositions({});
      setSuccess(false);
      setExistingResults([]);

      const { data: competition, error: competitionError } = await supabase
        .from('events')
        .select(`
          *,
          category:sports_categories(*),
          age_group:age_groups(*)
        `)
        .eq('reference_number', referenceNumber.trim())
        .single();

      if (competitionError) throw competitionError;
      if (!competition) throw new Error(t('mobileResults.errors.competitionNotFound'));

      if (competition.is_team_event) {
        const { data: teamResults, error: teamResultsError } = await supabase
          .from('team_results')
          .select(`
            *,
            house:houses(
              house_id,
              house_name,
              house_color
            )
          `)
          .eq('event_id', competition.event_id)
          .order('position', { ascending: true, nullsLast: true });

        if (teamResultsError) throw teamResultsError;

        if (teamResults && teamResults.length > 0) {
          const formattedResults: Result[] = teamResults.map(tr => ({
            result_id: tr.result_id,
            event_id: tr.event_id,
            student_id: tr.house_id,
            age_group_id: '',
            competition_year: tr.competition_year,
            position: tr.position,
            points_earned: tr.points_earned,
            created_at: tr.created_at,
            verified_by: tr.verified_by,
            student: {
              student_id: tr.house_id,
              first_name: tr.house?.house_name || 'Unknown',
              last_name: 'Team',
              date_of_birth: '',
              house: tr.house
            }
          }));
          
          setExistingResults(formattedResults);
          setCompetition(competition);
          setError(t('mobileResults.errors.alreadyRecorded'));
          
          const { data: housesData, error: housesError } = await supabase
            .from('houses')
            .select('*')
            .order('house_name');
            
          if (housesError) throw housesError;
          setHouses(housesData || []);
          
          return;
        }
        
        // Fetch team registrations
        const { data: teamRegistrations, error: teamRegistrationsError } = await supabase
          .from('team_registrations')
          .select(`
            *,
            house:houses(
              house_id,
              house_name,
              house_color
            )
          `)
          .eq('event_id', competition.event_id)
          .eq('status', 'confirmed');
          
        if (teamRegistrationsError) throw teamRegistrationsError;
        
        // If no confirmed team registrations, fetch all houses
        if (!teamRegistrations || teamRegistrations.length === 0) {
          const { data: housesData, error: housesError } = await supabase
            .from('houses')
            .select('*')
            .order('house_name');
            
          if (housesError) throw housesError;
          setHouses(housesData || []);
          
          const initialTeamPositions: Record<string, number | null> = {};
          housesData?.forEach(house => {
            initialTeamPositions[house.house_id] = null;
          });
          setTeamPositions(initialTeamPositions);
        } else {
          // Use the confirmed team registrations
          setHouses(teamRegistrations.map(reg => reg.house));
          
          const initialTeamPositions: Record<string, number | null> = {};
          teamRegistrations.forEach(reg => {
            initialTeamPositions[reg.house.house_id] = null;
          });
          setTeamPositions(initialTeamPositions);
        }
        
        setCompetition(competition);
      } else {
        const { data: results, error: resultsError } = await supabase
          .from('competition_results')
          .select(`
            *,
            student:students(
              student_id,
              first_name,
              last_name,
              first_name_tamil,
              last_name_tamil,
              reference_number,
              house:houses(
                house_name,
                house_color
              )
            )
          `)
          .eq('event_id', competition.event_id)
          .order('position', { ascending: true, nullsLast: true });

        if (resultsError) throw resultsError;

        if (results && results.length > 0) {
          setExistingResults(results);
          setCompetition(competition);
          setError(t('mobileResults.errors.alreadyRecorded'));
          return;
        }

        const { data: registrations, error: registrationsError } = await supabase
          .from('student_event_registrations')
          .select(`
            *,
            student:students(
              student_id,
              first_name,
              last_name,
              first_name_tamil,
              last_name_tamil,
              reference_number,
              date_of_birth,
              house:houses(
                house_name,
                house_color
              )
            )
          `)
          .eq('event_id', competition.event_id)
          .eq('status', 'confirmed');

        if (registrationsError) throw registrationsError;

        const initialPositions: Record<string, number | null> = {};
        registrations?.forEach(reg => {
          if (!results?.some(r => r.student_id === reg.student_id)) {
            initialPositions[reg.student_id] = null;
          }
        });

        setCompetition(competition);
        setParticipants(registrations || []);
        setPositions(initialPositions);
      }
    } catch (error: any) {
      console.error('Error searching competition:', error);
      setError(error.message || t('mobileResults.errors.competitionNotFound'));
    } finally {
      setLoading(false);
    }
  };

  const handlePositionChange = (studentId: string, position: string) => {
    const numPosition = position === '' ? null : parseInt(position);
    setPositions(prev => ({
      ...prev,
      [studentId]: numPosition
    }));
  };

  const handleTeamPositionChange = (houseId: string, position: string) => {
    const numPosition = position === '' ? null : parseInt(position);
    setTeamPositions(prev => ({
      ...prev,
      [houseId]: numPosition
    }));
  };

  const handleSaveResults = async () => {
    if (!competition) return;

    try {
      setSaving(true);
      setError(null);

      const currentYear = await getActiveSportsYear();

      if (competition.is_team_event) {
        const teamResultsToSave = Object.entries(teamPositions)
          .filter(([_, position]) => position !== null)
          .map(([houseId, position]) => {
            let points = competition.points_allocation.participation;
            if (position === 1) points = competition.points_allocation["1"];
            else if (position === 2) points = competition.points_allocation["2"];
            else if (position === 3) points = competition.points_allocation["3"];

            return {
              event_id: competition.event_id,
              house_id: houseId,
              position: position as number,
              points_earned: points,
              competition_year: currentYear,
              verified_by: 'mobile_results'
            };
          });

        if (teamResultsToSave.length === 0) {
          setError(t('mobileResults.errors.noResults'));
          return;
        }

        const { error: resultsError } = await supabase
          .from('team_results')
          .insert(teamResultsToSave);

        if (resultsError) throw resultsError;
        
        const { error: updateError } = await supabase
          .from('events')
          .update({ status: 'completed' })
          .eq('event_id', competition.event_id);

        if (updateError) throw updateError;
        
        const { data: updatedTeamResults } = await supabase
          .from('team_results')
          .select(`
            *,
            house:houses(
              house_id,
              house_name,
              house_color
            )
          `)
          .eq('event_id', competition.event_id)
          .order('position', { ascending: true, nullsLast: true });
        
        const formattedResults: Result[] = (updatedTeamResults || []).map(tr => ({
          result_id: tr.result_id,
          event_id: tr.event_id,
          student_id: tr.house_id,
          age_group_id: '',
          competition_year: tr.competition_year,
          position: tr.position,
          points_earned: tr.points_earned,
          created_at: tr.created_at,
          verified_by: tr.verified_by,
          student: {
            student_id: tr.house_id,
            first_name: tr.house?.house_name || 'Unknown',
            last_name: 'Team',
            date_of_birth: '',
            house: tr.house
          }
        }));
        
        const newTeamPositions = { ...teamPositions };
        teamResultsToSave.forEach(result => {
          delete newTeamPositions[result.house_id];
        });
        setTeamPositions(newTeamPositions);
        setExistingResults(formattedResults);
        setSuccess(true);
      } else {
        const resultsToSave = Object.entries(positions)
          .filter(([studentId, position]) => {
            return position !== null && !existingResults.some(r => 
              r.student_id === studentId && 
              r.event_id === competition.event_id && 
              r.competition_year === currentYear
            );
          })
          .map(([studentId, position]) => {
            let points = competition.points_allocation.participation;
            if (position === 1) points = competition.points_allocation["1"];
            else if (position === 2) points = competition.points_allocation["2"];
            else if (position === 3) points = competition.points_allocation["3"];

            return {
              event_id: competition.event_id,
              student_id: studentId,
              position: position as number,
              points_earned: points,
              competition_year: currentYear,
              age_group_id: competition.age_group_id!,
              verified_by: 'mobile_results'
            };
          });

        if (resultsToSave.length === 0) {
          setError(t('mobileResults.errors.noResults'));
          return;
        }

        const { error: resultsError } = await supabase
          .from('competition_results')
          .insert(resultsToSave);

        if (resultsError) throw resultsError;

        const { error: updateError } = await supabase
          .from('events')
          .update({ status: 'completed' })
          .eq('event_id', competition.event_id);

        if (updateError) throw updateError;

        const { data: updatedResults } = await supabase
          .from('competition_results')
          .select(`
            *,
            student:students(
              student_id,
              first_name,
              last_name,
              first_name_tamil,
              last_name_tamil,
              reference_number,
              house:houses(
                house_name,
                house_color
              )
            )
          `)
          .eq('event_id', competition.event_id)
          .order('position', { ascending: true, nullsLast: true });
        
        const newPositions = { ...positions };
        resultsToSave.forEach(result => {
          delete newPositions[result.student_id];
        });
        setPositions(newPositions);
        setExistingResults(updatedResults || []);
        setSuccess(true);
      }
      
      setCompetition(prev => prev ? { ...prev, status: 'completed' } : null);
    } catch (error: any) {
      console.error('Error saving results:', error);
      setError(error.message || 'Failed to save results');
    } finally {
      setSaving(false);
    }
  };

  if (!canManageResults) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-lg mx-auto space-y-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <h1 className="text-xl font-bold text-gray-900 mb-4">
            {t('mobileResults.title')}
          </h1>

          <div className="mb-6">
            <input
              type="text"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              placeholder={t('mobileResults.searchPlaceholder')}
              className="block w-full text-lg px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            />

            {!isVerified && (
              <div className="mt-4">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder={t('mobileResults.verificationCodePlaceholder')}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  maxLength={6}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowScanner(true)}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700"
            >
              <QrCode className="h-5 w-5" />
              {t('mobileResults.scanQrCode')}
            </button>
            
            <button
              onClick={verifyCode}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:bg-green-400"
            >
              <Search className="h-5 w-5" />
              {loading ? t('mobileResults.verifying') : t('mobileResults.verify')}
            </button>

            {referenceNumber && (
              <button
                onClick={clearSearch}
                className="flex items-center justify-center p-3 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300"
                aria-label={t('mobileResults.clearButton')}
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {showScanner && (
            <div className="mt-4">
              <div className="relative">
                <button
                  onClick={() => setShowScanner(false)}
                  className="absolute top-2 right-2 z-10 p-2 bg-white rounded-full shadow-md"
                >
                  <X className="h-5 w-5" />
                </button>
                <QrScanner
                  onDecode={handleDecode}
                  onError={handleError}
                  containerStyle={{ borderRadius: '0.5rem' }}
                />
              </div>
            </div>
          )}
        </div>

        {loading && (
          <div className="flex justify-center p-8">
            <LoadingSpinner />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
            <div className="flex">
              <Trophy className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-700">{t('mobileResults.success')}</p>
              </div>
            </div>
          </div>
        )}

        {competition && (
          <div className="bg-white rounded-lg shadow-md p-4 space-y-4">
            <div className="border-b pb-4">
              <h2 className="text-lg font-semibold text-gray-900">{competition.event_name}</h2>
              <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                <div className="text-sm font-medium text-gray-500">
                  {t('mobileResults.competitionDetails.reference')}
                </div>
                <div className="text-2xl font-bold text-blue-700 font-mono">
                  {competition.reference_number}
                </div>
              </div>
              <div className="mt-2">
                <p className="text-sm text-gray-600">
                  {t('mobileResults.competitionDetails.category')}: {competition.category?.category_name}
                </p>
                <p className="text-sm text-gray-600">
                  {t('mobileResults.competitionDetails.ageGroup')}: {competition.age_group?.group_name || 'All Age Groups (Team Event)'}
                </p>
                <p className="text-sm text-gray-600">
                  Status: {t(`competitions.status.${competition.status}`)}
                </p>
                <p className="text-sm text-gray-600">
                  Type: {competition.is_team_event ? 'Team Event' : 'Individual Event'}
                </p>
              </div>
            </div>

            {existingResults.length > 0 ? (
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">{t('mobileResults.existingResults.title')}:</h3>
                {existingResults.map((result) => (
                  <div
                    key={result.result_id}
                    className="border rounded-lg p-4 space-y-2"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {result.student?.first_name_tamil && result.student?.last_name_tamil ? (
                            `${result.student.first_name_tamil} ${result.student.last_name_tamil}`
                          ) : (
                            `${result.student?.first_name} ${result.student?.last_name}`
                          )}
                        </h3>
                        {!competition.is_team_event && (
                          <div className="text-sm font-mono text-blue-600">
                            {result.student?.reference_number}
                          </div>
                        )}
                        <div className="flex items-center mt-1">
                          <div
                            className="h-3 w-3 rounded-full mr-2"
                            style={{ backgroundColor: result.student?.house?.house_color }}
                          />
                          <span className="text-sm text-gray-600">
                            {result.student?.house?.house_name}
                          </span>
                        </div>
                      </div>
                      <div className="text-lg font-semibold">
                        {result.position || t('mobileResults.existingResults.participation')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : competition.is_team_event ? (
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Team Results:</h3>
                {houses.map((house) => (
                  <div
                    key={house.house_id}
                    className="border rounded-lg p-4 space-y-2"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <div
                          className="h-4 w-4 rounded-full mr-2"
                          style={{ backgroundColor: house.house_color }}
                        />
                        <h3 className="font-medium text-gray-900">
                          {house.house_name}
                        </h3>
                      </div>
                      {isVerified && (
                        <div className="flex items-center">
                          <input
                            type="number"
                            min="1"
                            max={houses.length}
                            value={teamPositions[house.house_id] || ''}
                            onChange={(e) => handleTeamPositionChange(house.house_id, e.target.value)}
                            className="w-20 px-3 py-2 border border-gray-300 rounded-md text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="#"
                          />
                          <button
                            onClick={() => {
                              const newTeamPositions = { ...teamPositions };
                              delete newTeamPositions[house.house_id];
                              setTeamPositions(newTeamPositions);
                            }}
                            className="ml-2 text-red-600 hover:text-red-800"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {isVerified && Object.keys(teamPositions).length > 0 && (
                  <div className="sticky bottom-0 bg-white p-4 border-t mt-4">
                    <button
                      onClick={handleSaveResults}
                      disabled={saving}
                      className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                    >
                      <Save className="h-5 w-5 mr-2" />
                      {saving ? t('mobileResults.saving') : t('mobileResults.saveButton')}
                    </button>
                  </div>
                )}
              </div>
            ) : participants.length === 0 ? (
              <div className="text-center py-8">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {t('mobileResults.noParticipants.title')}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t('mobileResults.noParticipants.description')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {participants.map((participant) => {
                  const hasPosition = positions[participant.student_id] !== undefined;
                  return (
                    <div
                      key={participant.student_id}
                      className="border rounded-lg p-4 space-y-2"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {participant.student.first_name_tamil && participant.student.last_name_tamil ? (
                              `${participant.student.first_name_tamil} ${participant.student.last_name_tamil}`
                            ) : (
                              `${participant.student.first_name} ${participant.student.last_name}`
                            )}
                          </h3>
                          <div className="text-sm font-mono text-blue-600">
                            {participant.student.reference_number}
                          </div>
                          <div className="flex items-center mt-1">
                            <div
                              className="h-3 w-3 rounded-full mr-2"
                              style={{ backgroundColor: participant.student.house?.house_color }}
                            />
                            <span className="text-sm text-gray-600">
                              {participant.student.house?.house_name}
                            </span>
                          </div>
                        </div>
                        {isVerified && (
                          <div className="flex items-center">
                            <input
                              type="number"
                              min="1"
                              max={participants.length}
                              value={positions[participant.student_id] || ''}
                              onChange={(e) => handlePositionChange(participant.student_id, e.target.value)}
                              className="w-20 px-3 py-2 border border-gray-300 rounded-md text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="#"
                            />
                            <button
                              onClick={() => {
                                const newPositions = { ...positions };
                                delete newPositions[participant.student_id];
                                setPositions(newPositions);
                              }}
                              className="ml-2 text-red-600 hover:text-red-800"
                            >
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {isVerified && Object.keys(positions).length > 0 && (
                  <div className="sticky bottom-0 bg-white p-4 border-t mt-4">
                    <button
                      onClick={handleSaveResults}
                      disabled={saving}
                      className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                    >
                      <Save className="h-5 w-5 mr-2" />
                      {saving ? t('mobileResults.saving') : t('mobileResults.saveButton')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileResults;